import api from '../services/api';

function getUserAgent() {
  if (typeof navigator === 'undefined') return '';
  return String(navigator.userAgent || navigator.vendor || '').toLowerCase();
}

export function isLikelyWebView() {
  if (typeof window === 'undefined') return false;
  const ua = getUserAgent();
  const androidWebView = ua.includes('wv') || /\bversion\/[\d.]+ chrome\/[\d.]+ mobile\b/.test(ua);
  const iosWebView = /iphone|ipad|ipod/.test(ua) && !ua.includes('safari');
  return androidWebView || iosWebView || !!window.ReactNativeWebView;
}

export function canUseBrowserPush() {
  if (typeof window === 'undefined') return false;
  if (isLikelyWebView()) return false;
  if (!window.isSecureContext) return false;
  return 'Notification' in window
    && 'serviceWorker' in navigator
    && 'PushManager' in window;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function getWorkerRegistration() {
  if (!canUseBrowserPush()) return null;
  const existing = await navigator.serviceWorker.getRegistration('/sw.js');
  if (existing) return existing;
  const registration = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  return registration;
}

export async function showBrowserNotification({
  title = 'TSLMS Notification',
  body = '',
  link = '/',
  tag = `tslms:${Date.now()}`,
  requireInteraction = true,
} = {}) {
  if (typeof window === 'undefined' || !('Notification' in window)) return false;
  if (Notification.permission !== 'granted') return false;

  try {
    const registration = await getWorkerRegistration();
    if (registration?.showNotification) {
      await registration.showNotification(title, {
        body,
        icon: '/favicon.png',
        badge: '/favicon.png',
        tag,
        renotify: true,
        requireInteraction,
        data: { url: link || '/' },
      });
      return true;
    }

    const instance = new Notification(title, {
      body,
      icon: '/favicon.png',
      tag,
      renotify: true,
    });
    instance.onclick = () => {
      try { window.focus(); } catch (_) {}
      try { window.location.href = link || '/'; } catch (_) {}
      try { instance.close(); } catch (_) {}
    };
    return true;
  } catch (err) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Push] Browser notification skipped:', err?.message || err);
    }
    return false;
  }
}

export async function getPushNotificationStatus() {
  const supported = canUseBrowserPush();

  if (!supported) {
    return {
      supported: false,
      webView: isLikelyWebView(),
      permission: 'unsupported',
      subscribed: false,
      serviceWorkerRegistered: false,
    };
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration('/sw.js');
    const subscription = registration
      ? await registration.pushManager.getSubscription()
      : null;

    return {
      supported: true,
      webView: false,
      permission: Notification.permission || 'default',
      subscribed: !!subscription,
      serviceWorkerRegistered: !!registration,
    };
  } catch (_) {
    return {
      supported: true,
      webView: false,
      permission: Notification.permission || 'default',
      subscribed: false,
      serviceWorkerRegistered: false,
    };
  }
}

/**
 * Register the service worker and subscribe to push notifications.
 * Safe to call multiple times — re-uses an existing subscription if present.
 * Returns true if successfully subscribed, false if unsupported or denied.
 */
export async function registerPushNotifications(options = {}) {
  const { requestPermission = true } = options;
  if (!canUseBrowserPush()) return false;

  // Don't ask again if already denied
  if (Notification.permission === 'denied') return false;

  try {
    const registration = await getWorkerRegistration();
    if (!registration) return false;

    // Only prompt when explicitly requested from a user action.
    if (Notification.permission !== 'granted' && requestPermission) {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return false;
    }
    if (Notification.permission !== 'granted') return false;

    // Fetch VAPID public key — endpoint is public so no token needed
    const keyRes   = await api.get('/shared/notifications/vapid-public-key');
    const publicKey = keyRes.data?.data?.publicKey;
    if (!publicKey) return false;

    // Reuse an existing subscription when possible.
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    // Persist subscription on server (upsert — safe to call on every login)
    await api.post('/shared/notifications/push-subscribe', { subscription });
    return true;
  } catch (err) {
    // Swallowed quietly — push is opt-in and not critical to app
    // functionality. Surface as a console.warn only, not error, so
    // devtools isn't flagged by extensions (e.g. installHook).
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Push] Registration skipped:', err?.message || err);
    }
    return false;
  }
}

export async function syncPushNotifications() {
  return registerPushNotifications({ requestPermission: false });
}

/**
 * Remove the push subscription from both the browser and the server.
 * Called on explicit logout so the server stops delivering to this browser.
 */
export async function unregisterPushNotifications() {
  if (!canUseBrowserPush()) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!registration) return;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;
    // Tell server first (while we still have a token)
    await api.delete('/shared/notifications/push-subscribe', {
      data: { endpoint: subscription.endpoint },
    }).catch(() => {});
    await subscription.unsubscribe();
  } catch (err) {
    console.error('[Push] Unregister failed:', err.message);
  }
}
